<div class="editable-review-table">
    <table class="editable-review-table__grid">
        <thead>
            <tr>
                @for (column of columns; track trackColumn($index, column)) {
                <th>
                    <span class="editable-review-table__th-text">{{ column.header }}</span>
                    @if (column.headerMetaText) {
                    @if (column.clickableToEdit) {
                    <app-tooltip [text]="clickToEditTooltipText">
                        <span class="editable-review-table__th-meta">{{ column.headerMetaText }}</span>
                    </app-tooltip>
                    } @else {
                    <span class="editable-review-table__th-meta">{{ column.headerMetaText }}</span>
                    }
                    }
                    @if (column.headerTooltip) {
                    <app-tooltip [text]="column.headerTooltip">
                        <mat-icon [svgIcon]="'icon-info-circle'"
                            class="editable-review-table__th-tooltip-icon"></mat-icon>
                        @if (hasOverrideOptions(column)) {
                        <ng-template #tooltipContent>
                            <div class="editable-review-table__override-tooltip">
                                <h4 class="editable-review-table__override-tooltip-title">{{ column.headerTooltip }}
                                </h4>
                                @for (option of getOptions(column); track option.value) {
                                <p class="editable-review-table__override-tooltip-label">{{ option.label }}:</p>
                                <p class="editable-review-table__override-tooltip-description">{{ option.description }}
                                </p>
                                }
                            </div>
                        </ng-template>
                        }
                    </app-tooltip>
                    }
                </th>
                }
            </tr>
        </thead>
        <tbody>
            @for (row of rows; track trackRow($index, row); let i = $index) {
            <tr [class]="getRowClass(row)">
                @for (column of columns; track trackColumn($index, column)) {
                <td [class.editable-review-table__cell--mismatch-value]="isMismatchValueCell(row, column.key)">
                    @switch (column.type) {

                    @case ('status') {
                    <button type="button" class="editable-review-table__copy-cell"
                        [class.editable-review-table__copy-cell--active]="isCopyEnabledForColumn(column)"
                        (click)="onReadOnlyCellClick(row, column, $event)">
                        <span [class]="getStatusClass(getCellText(row, column.key))">
                            @if (getStatusIcon(getCellText(row, column.key)); as statusIcon) {
                            <mat-icon [svgIcon]="statusIcon" class="editable-review-table__status-icon"></mat-icon>
                            }
                            {{ getStatusLabel(getCellText(row, column.key)) }}
                        </span>
                        @if (isCopyEnabledForColumn(column)) {
                        <button type="button" class="editable-review-table__copy-icon-btn"
                            (click)="onReadOnlyCellClick(row, column, $event)">
                            <mat-icon svgIcon="icon-copy-card" class="editable-review-table__copy-icon"></mat-icon>
                        </button>
                        }
                    </button>
                    }

                    @case ('percent') {
                    <button type="button" class="editable-review-table__copy-cell"
                        [class.editable-review-table__copy-cell--active]="isCopyEnabledForColumn(column)"
                        (click)="onReadOnlyCellClick(row, column, $event)">
                        <span>{{ getPercentValue(row, column.key) }}</span>
                        @if (isCopyEnabledForColumn(column)) {
                        <button type="button" class="editable-review-table__copy-icon-btn"
                            (click)="onReadOnlyCellClick(row, column, $event)">
                            <mat-icon svgIcon="icon-copy-card" class="editable-review-table__copy-icon"></mat-icon>
                        </button>
                        }
                    </button>
                    }

                    @case ('input') {
                    @if (isCellEditing(row.key, column.key)) {
                    <!-- Editing state: full draft/cancel logic from latest branch -->
                    <div class="editable-review-table__field-wrap editable-review-table__field-wrap--input editable-review-table__field-wrap--editing"
                        [class.editable-review-table__field-wrap--final-edit]="column.key === 'finalValue'"
                        [class.editable-review-table__field-wrap--error]="column.key === 'finalValue' && isFinalValueMissing(row) && row.status !== 'accepted'"
                        (focusout)="onCellFocusOut(row, i, column, $event)">
                        <mat-icon class="editable-review-table__edit-icon">edit</mat-icon>
                        <app-input [label]="''" [placeholder]="column.placeholder || ''" [isEdit]="true"
                            [isAutoFocus]="true" [ngModel]="getCellDraftValue(row, column)"
                            (ngModelChange)="onCellDraftChange(row, column, $event)">
                        </app-input>
                        <button type="button" class="editable-review-table__cancel-btn" title="Cancel edit"
                            (mousedown)="prepareCancelCellEdit(row, column, $event)"
                            (click)="cancelCellEdit(row, column, $event)">
                            <mat-icon [svgIcon]="'icon-close'"></mat-icon>
                        </button>
                    </div>
                    } @else {
                    <!-- Display state: copy-mode aware click handler -->
                    <button type="button" class="editable-review-table__final-value-trigger"
                        [class.editable-review-table__final-value-trigger--editable]="column.clickableToEdit && !isReadOnly"
                        [class.editable-review-table__final-value-trigger--error]="isFinalValueMissing(row) && row.status !== 'accepted'"
                        [class.editable-review-table__copy-cell--active]="isCopyEnabledForColumn(column)"
                        (click)="onFinalValueTriggerClick(row, column, $event)">
                        <app-tooltip [text]="getCellText(row, column.key)">
                            <span class="editable-review-table__text-ellipsis">{{ getCellText(row, column.key) }}</span>
                        </app-tooltip>
                        @if (isCopyEnabledForColumn(column)) {
                        <button type="button" class="editable-review-table__copy-icon-btn"
                            (click)="onFinalValueTriggerClick(row, column, $event)">
                            <mat-icon svgIcon="icon-copy-card" class="editable-review-table__copy-icon"></mat-icon>
                        </button>
                        }
                        @if (isFinalValueEdited(row, column)) {
                        <span class="editable-review-table__edited-badge">{{ editedBadgeText }}</span>
                        }
                    </button>
                    }
                    }

                    @case ('select') {
                    <!-- Select: error only when override reason is missing, not for final value errors -->
                    <div class="editable-review-table__field-wrap editable-review-table__field-wrap--select"
                        [class.editable-review-table__field-wrap--select-error]="column.key === 'overrideReason' && isOverrideReasonMissing(row)">
                        <app-dropdown [label]="''" [placeholder]="column.placeholder || 'Choose one'"
                            [options]="getDropdownOptions(column)" [multiselect]="false" [searchFlag]="false"
                            [value]="getCellText(row, column.key)" [disabled]="!isOverrideReasonEnabled(row)"
                            (selectionChange)="onCellValueChange(row, i, column.key, $event)">
                        </app-dropdown>
                    </div>
                    }

                    @default {
                    <button type="button" class="editable-review-table__copy-cell"
                        [class.editable-review-table__copy-cell--active]="isCopyEnabledForColumn(column)"
                        (click)="onReadOnlyCellClick(row, column, $event)">
                        @if (column.showIndex) {
                        <span>{{ i + 1 }}. {{ getCellText(row, column.key) }}</span>
                        } @else {
                        @if (column.key === 'submittedValue' || column.key === 'extractedValue' || column.key === 'finalValue') {
                        <app-tooltip [text]="getCellText(row, column.key)">
                            <span class="editable-review-table__text-ellipsis">{{ getCellText(row, column.key) }}</span>
                        </app-tooltip>
                        } @else {
                        <span>{{ getCellText(row, column.key) }}</span>
                        }
                        }
                        @if (isCopyEnabledForColumn(column)) {
                        <button type="button" class="editable-review-table__copy-icon-btn"
                            (click)="onReadOnlyCellClick(row, column, $event)">
                            <mat-icon svgIcon="icon-copy-card" class="editable-review-table__copy-icon"></mat-icon>
                        </button>
                        }
                    </button>
                    }

                    }
                </td>
                }
            </tr>
            }

            @if (rows.length === 0) {
            <tr>
                <td [attr.colspan]="columns.length" class="editable-review-table__empty-row">{{ emptyStateText }}</td>
            </tr>
            }
        </tbody>
    </table>
</div>